// Super Admin layout — left sidebar + content outlet.
// Styling follows the Vercel/Stripe-esque system we already ship (Inter,
// tight borders, no pills). CSS variables let the admin's own radius choice
// drive the look even here, so the panel always previews live changes.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, LayoutGrid, Sparkles, LifeBuoy, Palette, FileClock, ShieldCheck, Mail, FileText, Globe } from "lucide-react";
import { toast } from "sonner";
import WorkspacesTab from "./WorkspacesTab";
import AiPromptsTab from "./AiPromptsTab";
import GlobalSupportTab from "./GlobalSupportTab";
import MasterStylesTab from "./MasterStylesTab";
import EmailTemplatesTab from "./EmailTemplatesTab";
import AuditLogsTab from "./AuditLogsTab";
import PageIntrosTab from "./PageIntrosTab";
import BrandingTab from "./BrandingTab";
import TotpReauthModal from "./TotpReauthModal";

const TABS = [
  { key: "workspaces", label: "Workspaces", icon: LayoutGrid, Component: WorkspacesTab },
  { key: "ai_prompts", label: "AI Prompts", icon: Sparkles, Component: AiPromptsTab },
  { key: "support", label: "Global Support", icon: LifeBuoy, Component: GlobalSupportTab },
  { key: "intros", label: "Intro beheer", icon: FileText, Component: PageIntrosTab },
  { key: "branding", label: "Branding", icon: Globe, Component: BrandingTab },
  { key: "email", label: "Email Templates", icon: Mail, Component: EmailTemplatesTab },
  { key: "styles", label: "Master Styles", icon: Palette, Component: MasterStylesTab },
  { key: "audit", label: "Audit Logs", icon: FileClock, Component: AuditLogsTab },
];

export default function SuperAdminLayout() {
  const [active, setActive] = useState("workspaces");
  const navigate = useNavigate();

  const current = TABS.find((t) => t.key === active) || TABS[0];
  const Component = current.Component;

  const exitSuperAdmin = () => {
    localStorage.removeItem("bh_sa_token");
    toast.success("Super Admin sessie beëindigd");
    navigate("/brands");
  };

  return (
    <div className="min-h-screen bg-[#f8f8f6] text-[#1a1a1a] flex" data-testid="super-admin-layout">
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-gray-100">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-400 mb-1">
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.75} />
            Super Admin
          </div>
          <div className="font-display text-base font-medium">God Mode</div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                data-testid={`sa-tab-${t.key}`}
                className={`w-full flex items-center gap-2.5 px-3 h-9 rounded-[var(--global-radius)] text-sm transition-colors ${
                  isActive
                    ? "bg-[#1a1a1a] text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                {t.label}
              </button>
            );
          })}
        </nav>
        <button
          onClick={exitSuperAdmin}
          data-testid="sa-exit"
          className="mx-3 mb-4 h-9 rounded-[var(--global-radius)] border border-gray-200 text-xs text-gray-600 hover:text-[#1a1a1a] hover:border-gray-300 flex items-center justify-center gap-2"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.75} /> Afsluiten
        </button>
      </aside>
      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-8 py-10">
          <Component />
        </div>
      </main>
      <TotpReauthModal />
    </div>
  );
}
